import React, { useState, useEffect } from 'react';
// Import icons directly with type assertions
import { FiChevronDown, FiChevronUp, FiMail, FiCalendar, FiDollarSign, FiMapPin, FiPhone, FiCheckCircle } from 'react-icons/fi';

// Create typed icon components
const ChevronDownIcon = FiChevronDown as React.ComponentType<{ className?: string }>;
const ChevronUpIcon = FiChevronUp as React.ComponentType<{ className?: string }>;
const EnvelopeIcon = FiMail as React.ComponentType<{ className?: string }>;
const CalendarIcon = FiCalendar as React.ComponentType<{ className?: string }>;
const DollarIcon = FiDollarSign as React.ComponentType<{ className?: string }>;
const MapMarkerIcon = FiMapPin as React.ComponentType<{ className?: string }>;
const PhoneIcon = FiPhone as React.ComponentType<{ className?: string }>;
const CheckCircleIcon = FiCheckCircle as React.ComponentType<{ className?: string }>;

type LeadStatus = 'Hot Lead' | 'Warm Lead' | 'Cold Lead';

interface Keyword {
  text: string;
  importance: 'high' | 'medium' | 'low';
}

interface ClientScore {
  id: string;
  name: string;
  email: string;
  score: number;
  date: string;
  lastContact?: string;
  budget?: string;
  location?: string;
  phone?: string;
  status?: LeadStatus;
  tags?: string[];
  emailSnippet?: string;
  analysisDetails?: string;
  keywords?: Keyword[];
}

// Mock data for client scores
const mockScores: ClientScore[] = [
  {
    id: '1',
    name: 'Metro Realty Group',
    email: 'investments@metrerealty.com',
    score: 92,
    date: '2023-07-28',
    lastContact: '2 hours ago',
    budget: '$12,500,000',
    location: 'Lower Manhattan, NY',
    phone: '(212) 555-0123',
    status: 'Hot Lead',
    tags: ['Class A', 'Multi-family', 'Value-Add'],
    emailSnippet: 'Exclusive: Newly available 42-unit Class A multi-family in Lower Manhattan. NOI: $3.6M, 94% occupancy, built in 2018. Prime location near transportation and amenities. Offering memorandum available upon execution of NDA.',
    analysisDetails: 'High-quality asset in a prime Manhattan location with strong NOI. The property has been well-maintained and has upside potential through unit upgrades. The current cap rate of 4.2% is attractive for this market. The seller is motivated and open to creative deal structures.',
    keywords: [
      { text: 'Class A', importance: 'high' },
      { text: 'value-add', importance: 'high' },
      { text: 'prime location', importance: 'high' },
      { text: 'strong NOI', importance: 'high' }
    ]
  },
  {
    id: '2',
    name: 'Summit Capital Partners',
    email: 'acquisitions@summitcp.com',
    score: 88,
    date: '2023-07-27',
    lastContact: '5 hours ago',
    budget: '$28,000,000',
    location: 'Williamsburg, Brooklyn',
    phone: '(347) 555-9876',
    status: 'Hot Lead',
    tags: ['Development Site', 'Zoned Mixed-Use', 'Waterfront'],
    emailSnippet: 'Off-Market: 1.5-acre development site with 150K buildable SF in North Williamsburg. Zoned for mixed-use with waterfront views. Approved for 12 stories, 80/20 residential/commercial. Seeking JV partners or outright sale.',
    analysisDetails: 'Rare development opportunity in one of Brooklyn\'s most sought-after neighborhoods. The site has all necessary approvals in place, which significantly reduces development risk. The waterfront location commands premium pricing. Current zoning allows for significant density bonus through the affordable housing program.',
    keywords: [
      { text: 'development site', importance: 'high' },
      { text: 'waterfront', importance: 'high' },
      { text: 'approved plans', importance: 'high' },
      { text: 'mixed-use', importance: 'medium' }
    ]
  },
  {
    id: '3',
    name: 'Urban Core Investments',
    email: 'deals@urbancore.com',
    score: 75,
    date: '2023-07-25',
    lastContact: '2 days ago',
    budget: '$8,750,000',
    location: 'Long Island City, Queens',
    phone: '(718) 555-4567',
    status: 'Warm Lead',
    tags: ['Stabilized', 'Multi-tenant', 'Value-Add'],
    emailSnippet: 'Offering: 32,000 SF mixed-use asset in prime LIC. 100% occupied with strong credit tenants. Current NOI: $525K with 3% annual escalations. Seller motivated - will consider all offers.',
    analysisDetails: 'Stabilized asset in a growing Queens submarket with strong tenant covenants. The property has below-market rents with significant upside through lease rollovers. The area is experiencing significant development and gentrification, which should drive rent growth. The current cap rate of 6.0% is attractive for this type of asset.',
    keywords: [
      { text: 'stabilized', importance: 'high' },
      { text: 'credit tenants', importance: 'high' },
      { text: 'below-market rents', importance: 'medium' },
      { text: 'value-add', importance: 'medium' }
    ]
  },
  {
    id: '4',
    name: 'Harborview Capital',
    email: 'investor.relations@harborviewcap.com',
    score: 95,
    date: '2023-07-20',
    lastContact: '1 week ago',
    budget: '$45,000,000',
    location: 'Dumbo, Brooklyn',
    phone: '(917) 555-7890',
    status: 'Hot Lead',
    tags: ['Trophy Asset', 'Landmark Building', 'Triple-Net'],
    emailSnippet: 'Exclusive: Landmark office/retail building in heart of DUMBO. 85,000 SF, 100% leased to credit tenants with 12+ years WALT. Current NOI: $3.1M with 2.5% annual escalations. Rare opportunity to own a piece of Brooklyn history.',
    analysisDetails: 'Trophy asset in one of Brooklyn\'s most prestigious neighborhoods. The property features high ceilings, original architectural details, and panoramic views of Manhattan. The tenant roster includes well-known tech and creative firms. The long-term leases with credit tenants provide stable cash flow with built-in rent escalations.',
    keywords: [
      { text: 'trophy asset', importance: 'high' },
      { text: 'credit tenants', importance: 'high' },
      { text: 'long WALT', importance: 'high' },
      { text: 'landmark status', importance: 'medium' }
    ]
  },
  {
    id: '5',
    name: 'David Wilson',
    email: 'david@example.com',
    score: 61,
    date: '2023-07-23',
    analysisDetails:
      'Just starting the search process. Timeline is 6-12 months.',
  },
  {
    id: '6',
    name: 'Jennifer Martinez',
    email: 'jennifer@example.com',
    score: 89,
    date: '2023-07-22',
    analysisDetails:
      'Very motivated buyer with specific needs. Has financing in place.',
  },
  {
    id: '7',
    name: 'Robert Taylor',
    email: 'robert@example.com',
    score: 54,
    date: '2023-07-21',
    analysisDetails:
      'Initial inquiry but may not be ready to commit. Needs more information.',
  },
  {
    id: '8',
    name: 'Lisa Anderson',
    email: 'lisa@example.com',
    score: 82,
    date: '2023-07-20',
    analysisDetails:
      'Ready to buy, has been pre-approved. Looking in specific neighborhoods.',
  },
  {
    id: '9',
    name: 'Thomas White',
    email: 'thomas@example.com',
    score: 71,
    date: '2023-07-19',
    analysisDetails:
      'Motivated but has specific budget constraints. Might need alternative financing options.',
  },
  {
    id: '10',
    name: 'Jessica Clark',
    email: 'jessica@example.com',
    score: 95,
    date: '2023-07-18',
    analysisDetails:
      'Extremely motivated and financially ready. Looking for luxury properties with specific amenities.',
  },
];

const ClientScores: React.FC = () => {
  const [scores, setScores] = useState<ClientScore[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterScore, setFilterScore] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    // Simulate API call
    const fetchScores = async () => {
      try {
        // const response = await api.getClientScores();
        // setScores(response.data);

        // For now, use mock data
        setTimeout(() => {
          setScores(mockScores);
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error fetching client scores:', error);
        setIsLoading(false);
      }
    };

    fetchScores();
  }, []);

  const toggleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  const getScoreColorClass = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-400';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-400';
    return 'bg-red-100 text-red-800 border-red-400';
  };

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'Hot Lead':
        return 'bg-red-100 text-red-800';
      case 'Warm Lead':
        return 'bg-yellow-100 text-yellow-800';
      case 'Cold Lead':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderStars = (score: number) => {
    const stars = [];
    const filledStars = Math.round(score / 20); // Convert 0-100 to 0-5 stars
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <svg
          key={i}
          className={`h-4 w-4 ${i <= filledStars ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    
    return <div className="flex">{stars}</div>;
  };

  interface Keyword {
    text: string;
    importance: 'high' | 'medium' | 'low';
  }

  const highlightKeywords = (text: string, keywords: Keyword[] = []) => {
    if (!keywords || keywords.length === 0) return <>{text}</>;
    
    let highlightedText = text;
    const sortedKeywords = [...keywords].sort((a, b) => b.text.length - a.text.length);
    
    sortedKeywords.forEach((keyword: Keyword) => {
      const regex = new RegExp(`(${keyword.text})`, 'gi');
      highlightedText = highlightedText.replace(
        regex, 
        `<span class="font-bold ${keyword.importance === 'high' ? 'text-red-600' : keyword.importance === 'medium' ? 'text-yellow-600' : 'text-blue-600'}">$1</span>`
      );
    });
    
    return <div dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Filter clients based on search query and score filter
  const filteredScores = scores.filter((client: ClientScore) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      searchQuery === '' || 
      client.name.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      (client.location && client.location.toLowerCase().includes(query)) ||
      (client.tags && client.tags.some((tag: string) => tag.toLowerCase().includes(query)));
    
    const matchesFilter = 
      filterScore === 'all' ||
      (filterScore === 'high' && client.score >= 80) ||
      (filterScore === 'medium' && client.score >= 60 && client.score < 80) ||
      (filterScore === 'low' && client.score < 60);
    
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <div className="text-xl text-gray-600">Analyzing client data...</div>
          <p className="text-gray-500 mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Commercial Real Estate Deals</h1>
          <p className="mt-2 text-sm text-gray-600">Exclusive investment opportunities from top brokers and funds</p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center space-x-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={filterScore}
            onChange={(e) => setFilterScore(e.target.value as 'all' | 'high' | 'medium' | 'low')}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="all">All Properties</option>
            <option value="high">⭐ Core/Stabilized (80-100)</option>
            <option value="medium">🔹 Value-Add (60-79)</option>
            <option value="low">🏗️ Development/Distressed (1-59)</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Deal Overview
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {filteredScores.length} {filteredScores.length === 1 ? 'property' : 'properties'} found
            </p>
          </div>
          <div className="flex space-x-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {scores.filter(s => s.score >= 80).length} Core/Stabilized
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {scores.filter(s => s.score >= 60 && s.score < 80).length} Value-Add
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {scores.filter(s => s.score < 60).length} Development
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {filteredScores.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No properties found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter to find what you're looking for.
            </p>
          </div>
        ) : (
          filteredScores.map((lead) => (
            <div
              key={lead.id}
              className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-blue-500 hover:shadow-md transition-shadow duration-200"
            >
              <div className="px-4 py-5 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 text-lg font-semibold">
                        {lead.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          {lead.name}
                        </h3>
                        <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getScoreColorClass(lead.score)} border`}>
                          {lead.score}
                        </span>
                        {lead.status && (
                          <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(lead.status)}`}>
                            {lead.status === 'Hot Lead' ? 'Core/Stabilized' :
                             lead.status === 'Warm Lead' ? 'Value-Add' :
                             lead.status === 'Cold Lead' ? 'Development/Distressed' : lead.status}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <EnvelopeIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <PhoneIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {lead.phone}
                          </div>
                        )}
                        {lead.location && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <MapMarkerIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {lead.location}
                          </div>
                        )}
                        {lead.budget && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <DollarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {lead.budget}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 flex items-center">
                    <div className="flex items-center">
                      {renderStars(lead.score)}
                      <span className="ml-2 text-sm font-medium text-gray-500">
                        {lead.score}%
                      </span>
                    </div>
                    <button
                      onClick={() => toggleExpand(lead.id)}
                      className="ml-4 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {expandedId === lead.id ? (
                        <>
                          <span>Hide Property Details</span>
                          <ChevronUpIcon className="ml-1.5 h-3.5 w-3.5 text-gray-400" />
                        </>
                      ) : (
                        <>
                          <span>View Property Details</span>
                          <ChevronDownIcon className="ml-1.5 h-3.5 w-3.5 text-gray-400" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {lead.tags && lead.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {lead.tags.map((tag, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {expandedId === lead.id && (
                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
                    <div className="sm:col-span-2">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Offering Memorandum</h4>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <EnvelopeIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                              <p className="text-xs text-gray-500">{lead.lastContact || 'Today'}</p>
                            </div>
                            <div className="mt-1 text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">
                              {lead.emailSnippet ? (
                                highlightKeywords(lead.emailSnippet, lead.keywords)
                              ) : (
                                <p>No email content available</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Property Analysis</h4>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          {lead.analysisDetails ? (
                            <div className="prose prose-sm max-w-none">
                              {highlightKeywords(lead.analysisDetails, lead.keywords)}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No analysis details available</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Financial Highlights</h4>
                      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <dl className="space-y-6">
                            <div>
                              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Investment Grade</dt>
                              <dd className="mt-1 flex items-center">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                                  <div 
                                    className={`h-2.5 rounded-full ${lead.score >= 80 ? 'bg-green-500' : lead.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${lead.score}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium text-gray-900">{lead.score}/100</span>
                              </dd>
                            </div>
                            
                            <div>
                              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Interest Level</dt>
                              <dd className="mt-1 flex items-center">
                                <div className="flex items-center">
                                  {renderStars(lead.score)}
                                  <span className="ml-2 text-sm text-gray-900">
                                    {lead.score >= 80 ? 'High Interest' : lead.score >= 60 ? 'Moderate Interest' : 'Low Interest'}
                                  </span>
                                </div>
                              </dd>
                            </div>
                            
                            <div>
                              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Timeline</dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {lead.score >= 80 ? 'Ready to buy now' : lead.score >= 60 ? '1-3 months' : '3+ months'}
                              </dd>
                            </div>
                            
                            <div>
                              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Contacted</dt>
                              <dd className="mt-1 text-sm text-gray-900 flex items-center">
                                <CalendarIcon className="mr-1.5 h-4 w-4 text-gray-400" />
                                {new Date(lead.date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </dd>
                            </div>
                            
                            {lead.keywords && lead.keywords.length > 0 && (
                              <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                  Key Insights
                                </dt>
                                <dd className="space-y-2">
                                  {lead.keywords
                                    .filter(k => k.importance === 'high')
                                    .map((keyword, idx) => (
                                      <div key={idx} className="flex items-start">
                                        <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                        <span className="text-sm text-gray-700">
                                          {keyword.text.charAt(0).toUpperCase() + keyword.text.slice(1)}
                                        </span>
                                      </div>
                                    ))}
                                </dd>
                              </div>
                            )}
                            
                            <div className="pt-4 border-t border-gray-200">
                              <button
                                type="button"
                                className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <EnvelopeIcon className="mr-2 h-4 w-4" />
                                Request More Info
                              </button>
                              <button
                                type="button"
                                className="mt-2 w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <PhoneIcon className="mr-2 h-4 w-4" />
                                Schedule Property Tour
                              </button>
                            </div>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClientScores;
